/**
 * Canvas Component for UI-Model-1
 *
 * Main editing area displaying containers for the active page.
 * Supports:
 * - Container grid with row-based layout (rowIndex)
 * - Drag and drop for items from Component Library
 * - Drag and drop for submodels with all children
 * - Add container buttons (horizontal/vertical)
 * - Nested containers with DCA parity (row-based layout, order shifting, width redistribution)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { Droppable, Draggable, DroppableProvided, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import { useUIModelState } from '../../contexts/UIModelStateContext';
import { useContainerOperations } from '../../hooks/useContainerOperations';
import { useModule } from '../../contexts/ModuleContext';
import { canAddSubmodelItemToContainer } from '../../container-management/validators/submodelLockValidator';
import { useContainerLayout, useResponsiveContainerLayout } from '../../container-management';
import { CONTAINER_CONSTRAINTS } from '../../container-management/constants/constraints';
import { ContainerActionsMenu } from './ContainerActionsMenu';
import { ItemActionsMenu } from './ItemActionsMenu';
import { DropIndicator } from '../DropIndicator';
import { useDragDropContext, useDragDropContextOptional } from '../../contexts/DragDropContext';
import { calculateInsertIndexFromEvent } from '../../hooks/useDragDrop';
import type { LayoutMode } from '../../container-management';
import type { ContainerRow } from '../../container-management/types/container-row.types';
import {
  ContainerAPI,
  createFieldAPI,
  createWidgetAPI,
  createCategoryAPI,
  createConnectorAPI,
  buildDropDataConfiguration,
  FieldType,
  WidgetType,
  WidgetAPI,
  ItemAPI,
  ItemType,
  isSubmodelFull,
} from '../../types/api.types';
import { isItemAPI } from '../../types/state.types';
import { SubModelIcon, HideEyeIcon, EyeIconDisable } from 'assests/GmsIcons';
import { createAddItemCommand } from '../../types/command.types';
import { SubmodelChildItem } from '../ComponentLibrary';
import { WidgetRenderer } from '../Widgets';
import { FieldRenderer } from '../Fields';
import type { FieldAdditionalData } from '../Fields/types';
import { ConnectorPreview } from '../Connector';
import { CategoryPreview } from '../Category';
import { isFieldAPI, isConnectorAPI, isCategoryAPI, FieldAPI, ConnectorAPI, CategoryAPI } from '../../types/api.types';
import { ConnectorFieldConfig, createFieldConfig, CATEGORY_DEFAULT_CONFIG, CategoryFieldConfig } from '../../config/fieldDefaults';
import { createCategoryConfig } from '../../config/fieldDefaults/category.config';
import { createWidgetConfig } from '../../config/widgetDefaults';
import { createYourBuildConfig, mergeWithDefaults as mergeYourBuildWithDefaults } from '../../config/widgetDefaults/yourBuild.config';
import { useSaveQueue } from '../../contexts/SaveQueueContext';
import { isFreestyle } from '../../selectors';
import { generateInstanceId, getBaseId } from '../../utils/idGenerator';
import { ConfirmationDialog } from '../Common/ConfirmationDialog';
import { UsedFieldInfo } from '../../types/state.types';
import {
  buildHighlightMap,
  getHighlightClasses,
  getNestedHighlightClasses,
  ContainerHighlightLevel,
} from '../../utils/containerHighlight';

interface CanvasProps {
  id?: string;
}

// Generate unique ID for new items
const generateId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/** Get border styles based on container showBorder and border config */
const getContainerBorderStyles = (configuration?: ContainerAPI['configuration']): React.CSSProperties => {
  if (!configuration?.showBorder || !configuration.border || configuration.border.length === 0) {
    return {};
  }
  const borderValue = '3px solid #1F7AE4';
  if (configuration.border.includes('all')) {
    return { border: borderValue };
  }
  const styles: React.CSSProperties = {};
  if (configuration.border.includes('top')) styles.borderTop = borderValue;
  if (configuration.border.includes('right')) styles.borderRight = borderValue;
  if (configuration.border.includes('bottom')) styles.borderBottom = borderValue;
  if (configuration.border.includes('left')) styles.borderLeft = borderValue;
  return styles;
};

/** Collapse/expand chevron for collapsible containers */
const CollapseToggleIcon = ({ isCollapsed }: { isCollapsed: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    className={`tw-transition-transform tw-duration-200 ${isCollapsed ? '-tw-rotate-90' : ''}`}
  >
    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ============================================================================
// Extracted helpers — reduce duplication across Nested/Top-level containers
// ============================================================================

/**
 * Shared submodel header — one bar per real (non-temporary) submodel bound to the
 * container. Restricted containers have a single binding (`content.submodel`);
 * free-style mixed containers list ALL of them via `content.submodels`.
 */
const SubmodelHeader = ({
  container,
  onSubmodelSelect,
  selectedSubmodelId,
  excludeSubmodelId,
}: {
  container: ContainerAPI;
  onSubmodelSelect?: (submodelId: string, containerId: string) => void;
  selectedSubmodelId?: string | null;
  /** Skip this submodel (a nested container doesn't repeat its parent's bar) */
  excludeSubmodelId?: string;
}) => {
  const single = container.content?.submodel;
  const bound = (container.content?.submodels?.length
    ? container.content.submodels
    : single
      ? [single]
      : []
  )
    .filter(isSubmodelFull)
    .filter((sm) => !sm.isTemporary && sm.id !== excludeSubmodelId);

  if (bound.length === 0) return null;

  return (
    <>
      {bound.map((submodel) => (
        <div role="button" data-testid={`canvas-canvas-div-1-${submodel?.id ?? 0}`}
          key={submodel.id}
          onClick={(e) => {
            e.stopPropagation();
            onSubmodelSelect?.(submodel.id, container.id);
          }}
          className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-mb-2 tw-rounded tw-cursor-pointer tw-transition-all
            ${selectedSubmodelId === submodel.id
              ? 'tw-bg-accent-tint tw-border tw-border-accent'
              : 'tw-border tw-border-transparent hover:tw-bg-surface-hover'
            }`}
        >
          {/* SubModelIcon hardcodes stroke="#172B4D" — near-black on dark. */}
          <span aria-hidden="true" className="tw-text-ink-secondary [&_path]:tw-stroke-current icon-follow-ink">
            <SubModelIcon />
          </span>
          <span className="tw-text-sm tw-font-semibold tw-text-ink">
            {submodel.configuration?.showTitle !== false
              ? (submodel.configuration?.displayName || submodel.name)
              : null}
          </span>
        </div>
      ))}
    </>
  );
};

/** Shared item renderer — dispatches to type-specific preview components */
const ItemTypeRenderer = ({
  item,
  selectedItemId,
  onItemSelect,
  containerId,
  onContentChange,
}: {
  item: ItemAPI;
  selectedItemId?: string | null;
  onItemSelect?: (itemId: string, type: ItemType, containerId: string) => void;
  containerId: string;
  onContentChange?: (widgetId: string, content: string) => void;
}) => {
  const { type: itemType, id: itemId } = item;
  const { userProjectDataModels } = useModule();

  // Look up additionalData from model spec for field items (info/description for preview)
  let fieldAdditionalData: FieldAdditionalData | undefined;
  if (itemType === 'field') {
    const baseId = getBaseId(itemId);
    const spec = userProjectDataModels?.project;
    if (spec) {
      const topField = (spec.fields ?? []).find((f) => f.id === baseId);
      if (topField) {
        fieldAdditionalData = topField.data?.additionalData;
      } else {
        for (const submodel of (spec.dataModels ?? [])) {
          const subField = (submodel.data?.fields ?? []).find((f) => f.id === baseId);
          if (subField) {
            fieldAdditionalData = subField.additionalData;
            break;
          }
        }
      }
    }
  }

  if (itemType === 'widget') {
    return (
      <WidgetRenderer
        widget={item as WidgetAPI}
        isSelected={selectedItemId === itemId}
        onSelect={() => onItemSelect?.(itemId, itemType, containerId)}
        onContentChange={onContentChange}
      />
    );
  }

  if (itemType === 'field' && isFieldAPI(item)) {
    return (
      <FieldRenderer
        fieldType={(item as FieldAPI).fieldType}
        fieldName={(item as FieldAPI).name}
        config={(item as FieldAPI).configuration || {}}
        dataConfiguration={(item as FieldAPI).dataConfiguration}
        additionalData={fieldAdditionalData}
        isSelected={selectedItemId === itemId}
        onClick={() => onItemSelect?.(itemId, itemType, containerId)}
      />
    );
  }

  if (itemType === 'connector' && isConnectorAPI(item)) {
    return (
      <ConnectorPreview
        config={{
          label: (item as ConnectorAPI).name || 'Connector',
          displayName: (item as ConnectorAPI).name || 'Connector',
          showTitle: true,
          exclusionType: 'hide',
          required: false,
          connectorType: 'standard',
          ...((item as ConnectorAPI).configuration as Partial<ConnectorFieldConfig> || {}),
        }}
        fieldName={(item as ConnectorAPI).name}
        isSelected={selectedItemId === itemId}
        onClick={() => onItemSelect?.(itemId, itemType, containerId)}
      />
    );
  }

  if (itemType === 'category' && isCategoryAPI(item)) {
    return (
      <CategoryPreview
        config={{
          ...CATEGORY_DEFAULT_CONFIG,
          label: (item as CategoryAPI).name || 'Category',
          ...((item as CategoryAPI).configuration as Partial<CategoryFieldConfig> || {}),
        }}
        isSelected={selectedItemId === itemId}
        onClick={() => onItemSelect?.(itemId, itemType, containerId)}
        categoryId={(item as CategoryAPI).categoryId}
      />
    );
  }

  return (
    <div role="button" data-testid="canvas-canvas-div-2"
      onClick={(e) => {
        e.stopPropagation();
        onItemSelect?.(itemId, itemType, containerId);
      }}
      className={`tw-px-2 tw-py-1 tw-border tw-rounded tw-text-xs tw-cursor-pointer tw-transition-all ${
        selectedItemId === itemId
          ? 'tw-bg-accent-tint tw-border-accent tw-text-accent'
          : 'tw-bg-surface-sunken tw-border-edge tw-text-ink-secondary hover:tw-border-edge-strong'
      }`}
    >
      <span className="tw-font-medium">{itemType}:</span>{' '}
      {(item as { name?: string }).name || itemType}
    </div>
  );
};

/** Search through container hierarchy to find a nested container's parent and row */
const findNestedContainerInHierarchy = (
  topContainers: ContainerAPI[],
  nestedContainerId: string,
  getNestedRows: (containerId: string) => ContainerRow[],
): { parentId: string; rowIndex: number } | null => {
  for (const container of topContainers) {
    const nestedRows = getNestedRows(container.id);
    for (const row of nestedRows) {
      if (row.containers.find(c => c.id === nestedContainerId)) {
        return { parentId: container.id, rowIndex: row.rowIndex };
      }
      for (const nested of row.containers) {
        const deeperRows = getNestedRows(nested.id);
        for (const deeperRow of deeperRows) {
          if (deeperRow.containers.find(c => c.id === nestedContainerId)) {
            return { parentId: nested.id, rowIndex: deeperRow.rowIndex };
          }
        }
      }
    }
  }
  return null;
};

/** Recursively update widget content in container tree */
const updateWidgetContentInContainers = (
  containers: ContainerAPI[],
  widgetId: string,
  content: string,
): ContainerAPI[] =>
  containers.map((container) => {
    const items = container.content?.items || [];
    const idx = items.findIndex((i) => 'id' in i && i.id === widgetId);

    let updated = container;
    if (idx !== -1) {
      const widget = items[idx] as WidgetAPI;
      updated = {
        ...container,
        content: {
          ...container.content,
          items: [
            ...items.slice(0, idx),
            { ...widget, content } as WidgetAPI,
            ...items.slice(idx + 1),
          ],
        },
      };
    }

    if (updated.content?.nestedContainers?.length) {
      updated = {
        ...updated,
        content: {
          ...updated.content,
          nestedContainers: updateWidgetContentInContainers(updated.content.nestedContainers, widgetId, content),
        },
      };
    }

    return updated;
  });

/** Create an item from drop data — encapsulates type-specific factory logic */
const createDropItem = (
  parsedData: Record<string, unknown>,
  itemId: string,
): ItemAPI | null => {
  const { type, name, fieldType, widgetType, parentId, categoryId, dataConfiguration } = parsedData as {
    type: string; name?: string; fieldType?: string; widgetType?: string;
    parentId?: string; categoryId?: string; dataConfiguration?: unknown;
  };

  if (type === 'widget') {
    const wType = widgetType || 'contentBlock';
    if (wType === 'yourBuild') {
      return createWidgetAPI(
        `your-build-widget-instance-${crypto.randomUUID()}`,
        wType as WidgetType,
        parentId,
        { name: name || 'Your Build', configRef: 'yourBuildWidget' },
      );
    }
    const defaultConfig = createWidgetConfig(wType);
    const widgetOverrides: Partial<WidgetAPI> = { name: name || wType, configuration: defaultConfig };
    if (wType === 'contentBlock') {
      widgetOverrides.content = '';
    }
    return createWidgetAPI(itemId, wType as WidgetType, parentId, widgetOverrides);
  }

  if (type === 'field') {
    const fieldTypeStr = (fieldType as FieldType) || 'text';
    const defaultConfig = createFieldConfig(fieldTypeStr, name as string);
    const filteredDataConfig = buildDropDataConfiguration(fieldTypeStr, dataConfiguration as Record<string, unknown> | undefined);
    return createFieldAPI(itemId, name as string, fieldTypeStr, parentId, {
      dataConfiguration: filteredDataConfig,
      configuration: {
        ...defaultConfig,
        fieldName: name as string,
        fieldType: fieldTypeStr.toLowerCase().replace(/-/g, ''),
        displayName: name as string,
      },
    });
  }

  if (type === 'category') {
    const { categoryName, categoryType } = parsedData as { categoryName?: string; categoryType?: string };
    const catDisplayConfig = createCategoryConfig(name as string);
    return createCategoryAPI(itemId, name as string, (categoryId as string) || '', parentId, {
      configuration: {
        ...catDisplayConfig,
        categoryId: (categoryId as string) || '',
        categoryName: (categoryName as string) || (name as string),
        categoryType: (categoryType as string) || '',
        fieldName: name as string,
        fieldType: 'category',
        displayName: name as string,
        wrapperName: name as string,
        id: itemId,
        titleText: `Select ${name}`,
      },
    });
  }

  if (type === 'connector') {
    return createConnectorAPI(itemId, name as string, parentId);
  }

  console.warn('Unknown item type:', type);
  return null;
};

// Forward declaration for recursive rendering
interface NestedContainerRendererProps {
  parentId: string;
  nestedRows: ContainerRow[];
  canAddToNestedRow: (rowIndex: number) => boolean;
  onAddToRow: (rowIndex: number, insertAtOrder: number) => void;
  onAddAfterRow: (afterRowIndex: number) => void;
  onSelectNested: (nestedId: string) => void;
  selectedContainerId: string | null;
  depth?: number;
  maxDepth?: number;
  getNestedRowsForContainer?: (containerId: string) => ContainerRow[];
  canAddToNestedRowForContainer?: (containerId: string, rowIndex: number) => boolean;
  onAddNestedToRowForContainer?: (containerId: string, rowIndex: number, order: number) => void;
  onAddNestedAfterRowForContainer?: (containerId: string, afterRowIndex: number) => void;
  // Container action handlers for nested containers
  onDeleteContainer: (containerId: string) => void;
  onDuplicateInSameRow: (containerId: string) => void;
  onDuplicateInNewRow: (containerId: string) => void;
  canDuplicateNestedInSameRow: (containerId: string) => boolean;
  // Drop handler for nested containers
  onDrop?: (e: React.DragEvent, containerId: string) => void;
  // Drag highlight state (for drag-over visual feedback)
  dragHighlightedContainerId?: string | null;
  // Highlight level getter for hierarchical selection highlighting
  getHighlightLevel: (containerId: string) => ContainerHighlightLevel;
  // Item selection props for proper field rendering
  selectedItemId?: string | null;
  // Include containerId so nested containers can pass their actual ID
  onItemSelect?: (itemId: string, type: ItemType, containerId: string) => void;
  // Submodel selection props
  onSubmodelSelect?: (submodelId: string, containerId: string) => void;
  selectedSubmodelId?: string | null;
  // Content change handler for ContentBlockWidget in nested containers
  onContentChange?: (widgetId: string, content: string) => void;
  // Parent's submodel ID — used to suppress inherited submodel headers on children
  parentSubmodelId?: string;
}

// Nested container placeholder component (supports recursive nesting)
// NOTE: onSubmodelSelect and selectedSubmodelId props defined above in interface
const NestedContainerPlaceholder = ({
  container,
  highlightLevel,
  onSelect,
  depth,
  maxDepth,
  nestedRows,
  canAddToNestedRow,
  onAddNestedToRow,
  onAddNestedAfterRow,
  selectedContainerId,
  getNestedRowsForContainer,
  canAddToNestedRowForContainer,
  onAddNestedToRowForContainer,
  onAddNestedAfterRowForContainer,
  layoutMode = 'horizontal',
  onDeleteContainer,
  onDuplicateInSameRow,
  onDuplicateInNewRow,
  canDuplicateInSameRow,
  onDrop,
  isDragHighlighted = false,
  dragHighlightedContainerId,
  getHighlightLevel,
  selectedItemId = null,
  onItemSelect,
  onSubmodelSelect,
  selectedSubmodelId = null,
  onContentChange,
  parentSubmodelId,
}: {
  container: ContainerAPI;
  highlightLevel: ContainerHighlightLevel;
  onSelect: (id: string) => void;
  depth: number;
  maxDepth: number;
  nestedRows: ContainerRow[];
  canAddToNestedRow: (rowIndex: number) => boolean;
  onAddNestedToRow: (rowIndex: number, insertAtOrder: number) => void;
  onAddNestedAfterRow: (afterRowIndex: number) => void;
  selectedContainerId: string | null;
  getNestedRowsForContainer?: (containerId: string) => ContainerRow[];
  canAddToNestedRowForContainer?: (containerId: string, rowIndex: number) => boolean;
  onAddNestedToRowForContainer?: (containerId: string, rowIndex: number, order: number) => void;
  onAddNestedAfterRowForContainer?: (containerId: string, afterRowIndex: number) => void;
  layoutMode?: LayoutMode;
  // Container action handlers
  onDeleteContainer: (containerId: string) => void;
  onDuplicateInSameRow: (containerId: string) => void;
  onDuplicateInNewRow: (containerId: string) => void;
  canDuplicateInSameRow: boolean;
  // Drop handlers for nested container
  onDrop?: (e: React.DragEvent, containerId: string) => void;
  // Drag highlight state (for drag-over visual feedback)
  isDragHighlighted?: boolean;
  dragHighlightedContainerId?: string | null;
  // Highlight level getter for hierarchical selection highlighting
  getHighlightLevel: (containerId: string) => ContainerHighlightLevel;
  // Item selection props for proper field rendering
  selectedItemId?: string | null;
  // Include containerId so nested containers can pass their actual ID
  onItemSelect?: (itemId: string, type: ItemType, containerId: string) => void;
  // Submodel selection props
  onSubmodelSelect?: (submodelId: string, containerId: string) => void;
  selectedSubmodelId?: string | null;
  // Content change handler for ContentBlockWidget
  onContentChange?: (widgetId: string, content: string) => void;
  // Parent's submodel ID — used to suppress inherited submodel headers on children
  parentSubmodelId?: string;
}) => {
  const canNestDeeper = depth < maxDepth - 1;
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isCollapsible = container.configuration?.collapsible === true;

  // Drop handlers for nested container
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    onDrop?.(e, container.id);
  }, [onDrop, container.id]);

  // When in vertical mode, use 100% width; otherwise use configured width
  const containerWidth = layoutMode === 'vertical' ? '100%' : (container.configuration?.width || '100%');

  return (
    <div role="button" data-testid="canvas-canvas-div-3"
      onClick={(e) => {
        e.stopPropagation();
        onSelect(container.id);
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`tw-min-h-[60px] tw-min-w-0 tw-overflow-hidden tw-p-2 tw-border tw-rounded tw-cursor-pointer tw-transition-all ${
        getNestedHighlightClasses(highlightLevel, isDragOver || isDragHighlighted)
      }`}
      style={{
        width: containerWidth,
        maxWidth: '100%',
        ...getContainerBorderStyles(container.configuration),
      }}
    >
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-1">
        <div className={`tw-flex tw-items-center tw-gap-1 ${
          container.configuration?.showTitle === false ? 'tw-opacity-50' : ''
        }`}>
          {container.configuration?.showTitle === false && <span className="tw-text-ink-secondary [&_path]:tw-stroke-current"><EyeIconDisable /></span>}
          <h5 className="tw-text-xs tw-font-medium tw-text-ink-secondary">
            {container.name}
          </h5>
        </div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-ml-auto">
          {isCollapsible && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(!isCollapsed);
              }}
              className="tw-p-0.5 tw-rounded hover:tw-bg-surface-hover tw-text-ink-secondary hover:tw-text-ink tw-transition-colors"
              aria-label={isCollapsed ? 'Expand container' : 'Collapse container'}
              data-testid={`container-collapse-${container.id}`}
            >
              <CollapseToggleIcon isCollapsed={isCollapsed} />
            </button>
          )}
          <ContainerActionsMenu
            containerId={container.id}
            containerName={container.name}
            canDuplicateInSameRow={canDuplicateInSameRow}
            onDelete={onDeleteContainer}
            onDuplicateInSameRow={onDuplicateInSameRow}
            onDuplicateInNewRow={onDuplicateInNewRow}
          />
        </div>
      </div>
      {!isCollapsed && (
        <>
      <SubmodelHeader
        container={container}
        onSubmodelSelect={onSubmodelSelect}
        selectedSubmodelId={selectedSubmodelId}
        excludeSubmodelId={parentSubmodelId}
      />
      {/* Items display with Droppable/Draggable for cross-container drag support */}
      <Droppable droppableId={container.id} type="ITEM">
        {(droppableProvided, droppableSnapshot) => (
          <div
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
            className={`tw-min-h-[30px] ${droppableSnapshot.isDraggingOver ? 'tw-bg-accent-tint tw-rounded' : ''}`}
          >
            {container.content?.items && container.content.items.length > 0 ? (
              <div className="tw-space-y-1">
                {container.content.items.map((item, index) => {
                  if (!('type' in item)) return null;
                  const typedItem = item as ItemAPI;
                  const itemType = typedItem.type;
                  const itemId = typedItem.id;
                  const isVisible = typedItem.configuration?.isVisible !== false;

                  return (
                    <Draggable key={itemId} draggableId={itemId} index={index}>
                      {(draggableProvided, draggableSnapshot) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          className={`tw-group tw-relative ${
                            draggableSnapshot.isDragging ? 'tw-shadow-md tw-z-50' : ''
                          }`}
                          style={draggableProvided.draggableProps.style}
                        >
                          <div className={`tw-flex tw-items-center tw-gap-1 tw-min-w-0 ${!isVisible ? 'tw-opacity-40' : ''}`}>
                            {/* Drag handle - visible on hover */}
                            <div
                              {...draggableProvided.dragHandleProps}
                              className={`tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-flex-shrink-0 tw-cursor-grab tw-text-ink-tertiary tw-opacity-0
                                group-hover:tw-opacity-100 hover:tw-text-ink-secondary
                                ${draggableSnapshot.isDragging ? 'tw-opacity-100 tw-cursor-grabbing tw-text-accent' : ''}
                                tw-transition-opacity tw-duration-150`}
                              title="Drag to reorder"
                            >
                              <svg width="10" height="14" viewBox="0 0 12 16" fill="currentColor">
                                <circle cx="3" cy="3" r="1.5" />
                                <circle cx="9" cy="3" r="1.5" />
                                <circle cx="3" cy="8" r="1.5" />
                                <circle cx="9" cy="8" r="1.5" />
                                <circle cx="3" cy="13" r="1.5" />
                                <circle cx="9" cy="13" r="1.5" />
                              </svg>
                            </div>
                            {/* Hidden indicator icon */}
                            {!isVisible && (
                              <div className="tw-flex-shrink-0 tw-text-ink-secondary [&_path]:tw-stroke-current">
                                <HideEyeIcon />
                              </div>
                            )}
                            {/* Item content - conditional rendering by type */}
                            <div role="button" data-testid={`canvas-canvas-div-4-${index}`}
                              className={`tw-flex-1 tw-min-w-0 ${draggableSnapshot.isDragging ? 'tw-ring-2 tw-ring-accent' : ''}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ItemTypeRenderer
                                item={typedItem}
                                selectedItemId={selectedItemId}
                                onItemSelect={onItemSelect}
                                containerId={container.id}
                                onContentChange={onContentChange}
                              />
                            </div>
                          </div>
                          {/* Item actions menu — visible on hover */}
                          <div className="tw-absolute tw-top-0 tw-right-0 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-150 tw-z-10">
                            <ItemActionsMenu item={typedItem} />
                          </div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
              </div>
            ) : (
              <div className="tw-text-xs tw-text-ink-tertiary tw-text-center tw-py-2 tw-border tw-border-dashed tw-border-edge tw-rounded">
                Drop items
              </div>
            )}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>

      {/* Nested containers within nested (recursive) - only if we can nest deeper */}
      {canNestDeeper && (
        <NestedContainerRenderer
          parentId={container.id}
          nestedRows={nestedRows}
          canAddToNestedRow={canAddToNestedRow}
          onAddToRow={onAddNestedToRow}
          onAddAfterRow={onAddNestedAfterRow}
          onSelectNested={onSelect}
          selectedContainerId={selectedContainerId}
          depth={depth + 1}
          maxDepth={maxDepth}
          getNestedRowsForContainer={getNestedRowsForContainer}
          canAddToNestedRowForContainer={canAddToNestedRowForContainer}
          onAddNestedToRowForContainer={onAddNestedToRowForContainer}
          onAddNestedAfterRowForContainer={onAddNestedAfterRowForContainer}
          onDeleteContainer={onDeleteContainer}
          onDuplicateInSameRow={onDuplicateInSameRow}
          onDuplicateInNewRow={onDuplicateInNewRow}
          canDuplicateNestedInSameRow={(nestedId) => {
            // For deeper nested containers, check the parent row capacity
            const rows = getNestedRowsForContainer?.(container.id) || [];
            const nestedContainer = rows.flatMap(r => r.containers).find(c => c.id === nestedId);
            if (!nestedContainer) return false;
            return canAddToNestedRowForContainer?.(container.id, nestedContainer.rowIndex ?? 0) ?? false;
          }}
          onDrop={onDrop}
          dragHighlightedContainerId={dragHighlightedContainerId}
          getHighlightLevel={getHighlightLevel}
          selectedItemId={selectedItemId}
          onItemSelect={onItemSelect}
          onSubmodelSelect={onSubmodelSelect}
          selectedSubmodelId={selectedSubmodelId}
          onContentChange={onContentChange}
          parentSubmodelId={container.content?.submodel?.id}
        />
      )}
        </>
      )}
    </div>
  );
};

// Add button for nested containers (smaller variant)
const NestedAddButton = ({ id, orientation, onClick }: {
  id: string;
  orientation: 'horizontal' | 'vertical';
  onClick: () => void;
}) => {
  const isHorizontal = orientation === 'horizontal';

  if (isHorizontal) {
    return (
      <div
        id={id}
        data-testid={id}
        className="tw-relative tw-flex tw-items-center tw-group tw-h-4 tw-mt-1"
        title="Add nested container below"
      >
        <div className="tw-flex-grow tw-h-px tw-bg-transparent group-hover:tw-bg-accent tw-transition-colors tw-duration-200"></div>
        <div className="tw-px-1">
          <button data-testid="canvas-canvas-button-5"
            className="tw-w-4 tw-h-4 tw-rounded-full tw-bg-transparent tw-text-transparent
                       group-hover:tw-bg-accent group-hover:tw-text-ink-inverse
                       tw-flex tw-items-center tw-justify-center tw-shadow-none tw-cursor-pointer
                       group-hover:tw-shadow-sm tw-transition-all tw-duration-200 tw-text-xs"
            type="button"
            onClick={onClick}
          >
            +
          </button>
        </div>
        <div className="tw-flex-grow tw-h-px tw-bg-transparent group-hover:tw-bg-accent tw-transition-colors tw-duration-200"></div>
      </div>
    );
  }

  return (
    <div
      id={id}
      data-testid={id}
      className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-group tw-self-stretch tw-px-0.5 tw-min-h-[50px]"
      title="Add nested container beside"
    >
      <div className="tw-flex-1 tw-min-h-[20px] tw-w-px tw-bg-transparent group-hover:tw-bg-accent tw-transition-colors tw-duration-200"></div>
      <button data-testid="canvas-canvas-button-6"
        className="tw-w-4 tw-h-4 tw-rounded-full tw-bg-transparent tw-text-transparent
                   group-hover:tw-bg-accent group-hover:tw-text-ink-inverse
                   tw-flex tw-items-center tw-justify-center tw-shadow-none tw-cursor-pointer
                   group-hover:tw-shadow-sm tw-transition-all tw-duration-200 tw-text-xs tw-my-1"
        type="button"
        onClick={onClick}
      >
        +
      </button>
      <div className="tw-flex-1 tw-min-h-[20px] tw-w-px tw-bg-transparent group-hover:tw-bg-accent tw-transition-colors tw-duration-200"></div>
    </div>
  );
};

// Responsive wrapper for nested container rows
// Uses ResizeObserver to monitor width and switches to vertical layout when space is limited
const ResponsiveNestedRow = ({
  row,
  depth,
  maxDepth,
  parentId,
  onSelectNested,
  selectedContainerId,
  onAddToRow,
  onAddAfterRow,
  getNestedRowsForContainer,
  canAddToNestedRowForContainer,
  onAddNestedToRowForContainer,
  onAddNestedAfterRowForContainer,
  onDeleteContainer,
  onDuplicateInSameRow,
  onDuplicateInNewRow,
  canDuplicateNestedInSameRow,
  onDrop,
  dragHighlightedContainerId,
  getHighlightLevel,
  selectedItemId = null,
  onItemSelect,
  onSubmodelSelect,
  selectedSubmodelId = null,
  onContentChange,
  parentSubmodelId,
}: {
  row: ContainerRow;
  depth: number;
  maxDepth: number;
  parentId: string;
  onSelectNested: (nestedId: string) => void;
  selectedContainerId: string | null;
  onAddToRow: (rowIndex: number, insertAtOrder: number) => void;
  onAddAfterRow: (afterRowIndex: number) => void;
  getNestedRowsForContainer?: (containerId: string) => ContainerRow[];
  canAddToNestedRowForContainer?: (containerId: string, rowIndex: number) => boolean;
  onAddNestedToRowForContainer?: (containerId: string, rowIndex: number, order: number) => void;
  onAddNestedAfterRowForContainer?: (containerId: string, afterRowIndex: number) => void;
  // Container action handlers for nested containers
  onDeleteContainer: (containerId: string) => void;
  onDuplicateInSameRow: (containerId: string) => void;
  onDuplicateInNewRow: (containerId: string) => void;
  canDuplicateNestedInSameRow: (containerId: string) => boolean;
  // Drop handler for nested containers
  onDrop?: (e: React.DragEvent, containerId: string) => void;
  // Drag highlight state (for drag-over visual feedback)
  dragHighlightedContainerId?: string | null;
  // Highlight level getter for hierarchical selection highlighting
  getHighlightLevel: (containerId: string) => ContainerHighlightLevel;
  // Item selection props for proper field rendering
  selectedItemId?: string | null;
  // Include containerId so nested containers can pass their actual ID
  onItemSelect?: (itemId: string, type: ItemType, containerId: string) => void;
  // Submodel selection props
  onSubmodelSelect?: (submodelId: string, containerId: string) => void;
  selectedSubmodelId?: string | null;
  // Content change handler for ContentBlockWidget
  onContentChange?: (widgetId: string, content: string) => void;
  // Parent's submodel ID — used to suppress inherited submodel headers on children
  parentSubmodelId?: string;
}) => {
  const { containerRef, layoutMode } = useResponsiveContainerLayout({
    containerCount: row.containers.length,
    gapWidth: 4, // tw-gap-1 = 4px
  });

  const isVertical = layoutMode === 'vertical';

  return (
    <div>
      <div
        ref={containerRef}
        className={`tw-flex tw-gap-1 tw-items-stretch tw-overflow-hidden ${isVertical ? 'tw-flex-col' : 'tw-flex-row'}`}
      >
        {row.containers.map((nested, index) => (
          <React.Fragment key={nested.id}>
            <NestedContainerPlaceholder
              container={nested}
              highlightLevel={getHighlightLevel(nested.id)}
              onSelect={onSelectNested}
              depth={depth}
              maxDepth={maxDepth}
              nestedRows={getNestedRowsForContainer?.(nested.id) || []}
              canAddToNestedRow={(rowIdx) => canAddToNestedRowForContainer?.(nested.id, rowIdx) ?? false}
              onAddNestedToRow={(rowIdx, order) => onAddNestedToRowForContainer?.(nested.id, rowIdx, order)}
              onAddNestedAfterRow={(afterRowIdx) => onAddNestedAfterRowForContainer?.(nested.id, afterRowIdx)}
              selectedContainerId={selectedContainerId}
              getNestedRowsForContainer={getNestedRowsForContainer}
              canAddToNestedRowForContainer={canAddToNestedRowForContainer}
              onAddNestedToRowForContainer={onAddNestedToRowForContainer}
              onAddNestedAfterRowForContainer={onAddNestedAfterRowForContainer}
              layoutMode={layoutMode}
              onDeleteContainer={onDeleteContainer}
              onDuplicateInSameRow={onDuplicateInSameRow}
              onDuplicateInNewRow={onDuplicateInNewRow}
              canDuplicateInSameRow={row.canAddMore}
              onDrop={onDrop}
              isDragHighlighted={dragHighlightedContainerId === nested.id}
              dragHighlightedContainerId={dragHighlightedContainerId}
              getHighlightLevel={getHighlightLevel}
              selectedItemId={selectedItemId}
              onItemSelect={onItemSelect}
              onSubmodelSelect={onSubmodelSelect}
              selectedSubmodelId={selectedSubmodelId}
              onContentChange={onContentChange}
              parentSubmodelId={parentSubmodelId}
            />
            {/* Vertical "+" button - only show when horizontal and row can add more */}
            {!isVertical && row.canAddMore && (
              <NestedAddButton
                id={`nested-add-vertical-${parentId}-${row.rowIndex}-${index}`}
                orientation="vertical"
                onClick={() => onAddToRow(row.rowIndex, nested.order + 1)}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      {/* Horizontal "+" button below nested row */}
      <NestedAddButton
        id={`nested-add-horizontal-${parentId}-${row.rowIndex}`}
        orientation="horizontal"
        onClick={() => onAddAfterRow(row.rowIndex)}
      />
    </div>
  );
};

// Nested container renderer with row-based layout (supports recursive depth tracking)
const NestedContainerRenderer = ({
  parentId,
  nestedRows,
  canAddToNestedRow,
  onAddToRow,
  onAddAfterRow,
  onSelectNested,
  selectedContainerId,
  depth = 1,
  maxDepth = 3,
  getNestedRowsForContainer,
  canAddToNestedRowForContainer,
  onAddNestedToRowForContainer,
  onAddNestedAfterRowForContainer,
  onDeleteContainer,
  onDuplicateInSameRow,
  onDuplicateInNewRow,
  canDuplicateNestedInSameRow,
  onDrop,
  dragHighlightedContainerId,
  getHighlightLevel,
  selectedItemId = null,
  onItemSelect,
  onSubmodelSelect,
  selectedSubmodelId = null,
  onContentChange,
  parentSubmodelId,
}: NestedContainerRendererProps) => {
  return (
    <div className="tw-mt-2 tw-pl-3 tw-border-l-2 tw-border-edge tw-space-y-1">
      {nestedRows.length > 0 ? (
        nestedRows.map((row) => (
          <ResponsiveNestedRow
            key={row.rowIndex}
            row={row}
            depth={depth}
            maxDepth={maxDepth}
            parentId={parentId}
            onSelectNested={onSelectNested}
            selectedContainerId={selectedContainerId}
            onAddToRow={onAddToRow}
            onAddAfterRow={onAddAfterRow}
            getNestedRowsForContainer={getNestedRowsForContainer}
            canAddToNestedRowForContainer={canAddToNestedRowForContainer}
            onAddNestedToRowForContainer={onAddNestedToRowForContainer}
            onAddNestedAfterRowForContainer={onAddNestedAfterRowForContainer}
            onDeleteContainer={onDeleteContainer}
            onDuplicateInSameRow={onDuplicateInSameRow}
            onDuplicateInNewRow={onDuplicateInNewRow}
            canDuplicateNestedInSameRow={canDuplicateNestedInSameRow}
            onDrop={onDrop}
            dragHighlightedContainerId={dragHighlightedContainerId}
            getHighlightLevel={getHighlightLevel}
            selectedItemId={selectedItemId}
            onItemSelect={onItemSelect}
            onSubmodelSelect={onSubmodelSelect}
            selectedSubmodelId={selectedSubmodelId}
            onContentChange={onContentChange}
            parentSubmodelId={parentSubmodelId}
          />
        ))
      ) : (
        /* If no nested containers yet, show single add button */
        <div className="tw-py-1">
          <button data-testid="canvas-canvas-button-7"
            onClick={() => onAddAfterRow(-1)}
            className="tw-w-full tw-py-1 tw-text-xs tw-text-accent tw-border tw-border-dashed tw-border-accent tw-rounded hover:tw-bg-accent-tint tw-transition-colors"
          >
            + Add nested container
          </button>
        </div>
      )}
    </div>
  );
};

// Container component with drop zone
const ContainerPlaceholder = ({
  container,
  onSelect,
  highlightLevel,
  onDrop,
  selectedItemId,
  onItemSelect,
  nestedRows,
  canAddToNestedRow,
  onAddNestedToRow,
  onAddNestedAfterRow,
  selectedContainerId,
  maxNestingDepth,
  getNestedRowsForContainer,
  canAddToNestedRowForContainer,
  onAddNestedToRowForContainer,
  onAddNestedAfterRowForContainer,
  onDeleteContainer,
  onDuplicateInSameRow,
  onDuplicateInNewRow,
  canDuplicateInSameRow,
  canDuplicateNestedInSameRow,
  dragHighlightedContainerId,
  getHighlightLevel,
  onSubmodelSelect,
  selectedSubmodelId = null,
  onContentChange,
}: {
  container: ContainerAPI;
  onSelect: (id: string) => void;
  highlightLevel: ContainerHighlightLevel;
  onDrop: (e: React.DragEvent, containerId: string) => void;
  selectedItemId: string | null;
  onItemSelect: (itemId: string, itemType: ItemType, containerId: string) => void;
  nestedRows: ContainerRow[];
  canAddToNestedRow: (rowIndex: number) => boolean;
  onAddNestedToRow: (rowIndex: number, insertAtOrder: number) => void;
  onAddNestedAfterRow: (afterRowIndex: number) => void;
  selectedContainerId: string | null;
  maxNestingDepth: number;
  getNestedRowsForContainer: (containerId: string) => ContainerRow[];
  canAddToNestedRowForContainer: (containerId: string, rowIndex: number) => boolean;
  onAddNestedToRowForContainer: (containerId: string, rowIndex: number, order: number) => void;
  onAddNestedAfterRowForContainer: (containerId: string, afterRowIndex: number) => void;
  onDeleteContainer: (containerId: string) => void;
  onDuplicateInSameRow: (containerId: string) => void;
  onDuplicateInNewRow: (containerId: string) => void;
  canDuplicateInSameRow: boolean;
  canDuplicateNestedInSameRow: (containerId: string) => boolean;
  // Drag highlight state (for drag-over visual feedback)
  dragHighlightedContainerId?: string | null;
  // Highlight level getter for hierarchical selection highlighting
  getHighlightLevel: (containerId: string) => ContainerHighlightLevel;
  // Submodel selection props
  onSubmodelSelect?: (submodelId: string, containerId: string) => void;
  selectedSubmodelId?: string | null;
  // Content change handler for ContentBlockWidget
  onContentChange?: (widgetId: string, content: string) => void;
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isCollapsible = container.configuration?.collapsible === true;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    // Validation happens in the parent handleDrop - it shows toast on invalid
    onDrop(e, container.id);
  };

  return (
    <div role="button"
      id={`container-${container.id}`}
      data-testid={`container-${container.id}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(container.id);
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`tw-min-h-[100px] tw-p-4 tw-border tw-rounded tw-cursor-pointer tw-transition-all ${
        getHighlightClasses(highlightLevel, isDragOver || dragHighlightedContainerId === container.id)
      }`}
      style={{
        width: container.configuration?.width || '100%',
        minWidth: `${CONTAINER_CONSTRAINTS.MIN_CONTAINER_WIDTH_PX}px`,
        ...getContainerBorderStyles(container.configuration),
      }}
    >
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
        <div className={`tw-flex tw-items-center tw-gap-1.5 ${
          container.configuration?.showTitle === false ? 'tw-opacity-50' : ''
        }`}>
          {container.configuration?.showTitle === false && <span className="tw-text-ink-secondary [&_path]:tw-stroke-current"><EyeIconDisable /></span>}
          <h4 className="tw-text-sm tw-font-medium tw-text-ink">
            {container.name}
          </h4>
        </div>
        <div className="tw-flex tw-items-center tw-gap-1 tw-ml-auto">
          {isCollapsible && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(!isCollapsed);
              }}
              className="tw-p-0.5 tw-rounded hover:tw-bg-surface-hover tw-text-ink-secondary hover:tw-text-ink tw-transition-colors"
              aria-label={isCollapsed ? 'Expand container' : 'Collapse container'}
              data-testid={`container-collapse-${container.id}`}
            >
              <CollapseToggleIcon isCollapsed={isCollapsed} />
            </button>
          )}
          <ContainerActionsMenu
            containerId={container.id}
            containerName={container.name}
            canDuplicateInSameRow={canDuplicateInSameRow}
            onDelete={onDeleteContainer}
            onDuplicateInSameRow={onDuplicateInSameRow}
            onDuplicateInNewRow={onDuplicateInNewRow}
          />
        </div>
      </div>

      {!isCollapsed && (
        <>
      <SubmodelHeader container={container} onSubmodelSelect={onSubmodelSelect} selectedSubmodelId={selectedSubmodelId} />

      {/* Items display with Droppable/Draggable */}
      <Droppable droppableId={container.id} type="ITEM">
        {(droppableProvided: DroppableProvided, droppableSnapshot) => (
          <div
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
            className={`tw-min-h-[40px] ${
              droppableSnapshot.isDraggingOver && !isDragOver
                ? 'tw-bg-accent-tint tw-rounded'
                : ''
            }`}
          >
            {container.content?.items && container.content.items.length > 0 ? (
              <div className="tw-space-y-2">
                {container.content.items
                  .filter((item): item is ItemAPI => isItemAPI(item))
                  .map((item, index) => {
                    const itemType = item.type;
                    const itemId = item.id;
                    const isVisible = item.configuration?.isVisible !== false;

                    return (
                      <Draggable
                        key={itemId}
                        draggableId={itemId}
                        index={index}
                      >
                        {(draggableProvided: DraggableProvided, draggableSnapshot: DraggableStateSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            data-item-id={itemId}
                            className={`tw-group tw-relative ${
                              draggableSnapshot.isDragging
                                ? 'tw-shadow-lg tw-z-50'
                                : ''
                            }`}
                            style={draggableProvided.draggableProps.style}
                          >
                            <div className={`tw-flex tw-items-start tw-gap-1 ${!isVisible ? 'tw-opacity-40' : ''}`}>
                              {/* Drag handle */}
                              <div
                                {...draggableProvided.dragHandleProps}
                                className={`tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-flex-shrink-0 tw-cursor-grab tw-text-ink-tertiary tw-opacity-0 tw-mt-0.5
                                  group-hover:tw-opacity-100 hover:tw-text-ink-secondary
                                  ${draggableSnapshot.isDragging ? 'tw-opacity-100 tw-cursor-grabbing tw-text-accent' : ''}
                                  tw-transition-opacity tw-duration-150`}
                                title="Drag to reorder"
                              >
                                <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor">
                                  <circle cx="3" cy="3" r="1.5" />
                                  <circle cx="9" cy="3" r="1.5" />
                                  <circle cx="3" cy="8" r="1.5" />
                                  <circle cx="9" cy="8" r="1.5" />
                                  <circle cx="3" cy="13" r="1.5" />
                                  <circle cx="9" cy="13" r="1.5" />
                                </svg>
                              </div>
                              {/* Hidden indicator icon */}
                              {!isVisible && (
                                <div className="tw-flex-shrink-0 tw-mt-0.5 tw-text-ink-secondary [&_path]:tw-stroke-current">
                                  <HideEyeIcon />
                                </div>
                              )}

                              {/* Item content */}
                              <div className="tw-flex-1 tw-min-w-0">
                                <ItemTypeRenderer
                                  item={item}
                                  selectedItemId={selectedItemId}
                                  onItemSelect={onItemSelect}
                                  containerId={container.id}
                                  onContentChange={onContentChange}
                                />
                              </div>
                            </div>
                            {/* Item actions menu — visible on hover */}
                            <div className="tw-absolute tw-top-0 tw-right-0 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-150 tw-z-10">
                              <ItemActionsMenu item={item} />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
              </div>
            ) : (
              <div
                className={`tw-text-center tw-py-4 tw-border-2 tw-border-dashed tw-rounded ${
                  isDragOver || droppableSnapshot.isDraggingOver
                    ? 'tw-border-accent tw-text-accent'
                    : 'tw-border-edge tw-text-ink-tertiary'
                }`}
              >
                <p className="tw-text-sm">
                  {isDragOver || droppableSnapshot.isDraggingOver
                    ? 'Drop component here'
                    : 'Drag components here'}
                </p>
              </div>
            )}
            {droppableProvided.placeholder}
          </div>
        )}
      </Droppable>

      {/* Nested containers with row-based layout */}
      {(container.content?.nestedContainers && container.content.nestedContainers.length > 0) || nestedRows.length >= 0 ? (
        <NestedContainerRenderer
          parentId={container.id}
          nestedRows={nestedRows}
          canAddToNestedRow={canAddToNestedRow}
          onAddToRow={onAddNestedToRow}
          onAddAfterRow={onAddNestedAfterRow}
          onSelectNested={onSelect}
          selectedContainerId={selectedContainerId}
          depth={1}
          maxDepth={maxNestingDepth}
          getNestedRowsForContainer={getNestedRowsForContainer}
          canAddToNestedRowForContainer={canAddToNestedRowForContainer}
          onAddNestedToRowForContainer={onAddNestedToRowForContainer}
          onAddNestedAfterRowForContainer={onAddNestedAfterRowForContainer}
          onDeleteContainer={onDeleteContainer}
          onDuplicateInSameRow={onDuplicateInSameRow}
          onDuplicateInNewRow={onDuplicateInNewRow}
          canDuplicateNestedInSameRow={canDuplicateNestedInSameRow}
          onDrop={onDrop}
          dragHighlightedContainerId={dragHighlightedContainerId}
          getHighlightLevel={getHighlightLevel}
          selectedItemId={selectedItemId}
          onItemSelect={(itemId, type, nestedContainerId) => onItemSelect(itemId, type as ItemType, nestedContainerId)}
          onSubmodelSelect={onSubmodelSelect}
          selectedSubmodelId={selectedSubmodelId}
          onContentChange={onContentChange}
          parentSubmodelId={container.content?.submodel?.id}
        />
      ) : null}
        </>
      )}
    </div>
  );
};

// Add container button with elegant group-hover design
const AddContainerButton = ({ id, orientation, onClick }: {
  id: string;
  orientation: 'horizontal' | 'vertical';
  onClick: () => void;
}) => {
  const isHorizontal = orientation === 'horizontal';

  if (isHorizontal) {
    // Horizontal orientation - line across with button in center
    return (
      <div
        id={id}
        data-testid={id}
        className="tw-relative tw-flex tw-items-center tw-group tw-h-5 tw-mb-0.5"
        title="Add container below"
      >
        <div className="tw-flex-grow tw-h-px tw-bg-transparent group-hover:tw-bg-accent tw-transition-colors tw-duration-200"></div>
        <div className="tw-px-2">
          <button data-testid="canvas-canvas-button-9"
            className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-transparent tw-text-transparent
                       group-hover:tw-bg-accent group-hover:tw-text-ink-inverse
                       tw-flex tw-items-center tw-justify-center tw-shadow-none tw-cursor-pointer
                       group-hover:tw-shadow-md tw-transition-all tw-duration-200"
            type="button"
            onClick={onClick}
          >
            <span className="tw-text-base">+</span>
          </button>
        </div>
        <div className="tw-flex-grow tw-h-px tw-bg-transparent group-hover:tw-bg-accent tw-transition-colors tw-duration-200"></div>
      </div>
    );
  }

  // Vertical orientation - line down with button in center
  return (
    <div
      id={id}
      data-testid={id}
      className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-group tw-self-stretch tw-px-1 tw-min-h-full"
      title="Add container beside"
    >
      <div className="tw-flex-1 tw-min-h-[40px] tw-w-px tw-bg-transparent group-hover:tw-bg-accent tw-transition-colors tw-duration-200"></div>
      <button data-testid="canvas-canvas-button-10"
        className="tw-w-5 tw-h-5 tw-rounded-full tw-bg-transparent tw-text-transparent
                   group-hover:tw-bg-accent group-hover:tw-text-ink-inverse
                   tw-flex tw-items-center tw-justify-center tw-shadow-none tw-cursor-pointer
                   group-hover:tw-shadow-md tw-transition-all tw-duration-200 tw-my-2"
        type="button"
        onClick={onClick}
      >
        <span className="tw-text-base">+</span>
      </button>
      <div className="tw-flex-1 tw-min-h-[40px] tw-w-px tw-bg-transparent group-hover:tw-bg-accent tw-transition-colors tw-duration-200"></div>
    </div>
  );
};

export const Canvas = ({ id }: CanvasProps) => {
  const baseId = id || 'canvas';
  const {
    state,
    dispatch,
    isSubmodelUsedElsewhere,
    isFieldUsed,
    getFieldUsageInfo,
    isCategoryUsed,
    getCategoryUsageInfo,
    isConnectorUsed,
    getConnectorUsageInfo,
  } = useUIModelState();
  const { getSubmodelInstanceLimits } = useModule();
  const freestyle = isFreestyle(state.model);

  // Free style: a drop of an already-placed item waits here for the user's
  // "add again?" confirmation before it is committed to the canvas.
  const [pendingDuplicateDrop, setPendingDuplicateDrop] = useState<{
    data: Record<string, unknown>;
    containerId: string;
    insertIndex: number;
    usage: UsedFieldInfo;
  } | null>(null);
  const {
    activePageId,
    selectedContainerId,
    selectedItemId,
    selectedSubmodelId,
  } = state.editorState;

  const { enqueueSave } = useSaveQueue();

  // Get drag drop context for container highlight state (optional - may not be wrapped)
  const dragDropContext = useDragDropContextOptional();
  const dragHighlightedContainerId = dragDropContext?.containerHighlight?.isActive
    ? dragDropContext.containerHighlight.containerId
    : null;

  // Compute hierarchical highlight levels for all containers in selection path
  const highlightMap = useMemo(() => {
    return buildHighlightMap(state, selectedContainerId);
  }, [state, selectedContainerId]);

  // Helper to get highlight level for any container
  const getHighlightLevel = useCallback((containerId: string): ContainerHighlightLevel => {
    return highlightMap.get(containerId) || null;
  }, [highlightMap]);

  // Get container operations for submodel handling and container actions
  const { addSubmodelWithChildren, hasSubmodelChildInContainer, addSubmodelToContainer, removeContainer, duplicateContainer } = useContainerOperations();

  // Get container layout operations with row management
  const {
    containerRows,
    canAddToRowIndex,
    addContainerToRow,
    addContainerAfterRow,
    getNextContainerNumber,
    getNestedContainerRows,
    canAddToNestedRow,
    getNestedContainerName,
    addNestedContainer,
    addNestedContainerAfterRow,
    MAX_CONTAINERS_PER_ROW,
    MAX_NESTING_DEPTH,
  } = useContainerLayout();

  // Get canvas for active page
  const canvas = activePageId
    ? Object.values(state.model?.layout.canvas || {}).find(
        (c) => c.pageId === activePageId
      )
    : null;

  const containers = canvas?.containers || [];

  const handleContainerSelect = (containerId: string) => {
    dispatch({
      type: 'SET_SELECTED_CONTAINER',
      payload: { containerId },
    });
  };

  /**
   * Delete a container with toast notification
   */
  const handleDeleteContainer = useCallback((containerId: string) => {
    removeContainer(containerId);
    toast.success('Container deleted');
  }, [removeContainer]);

  /**
   * Check if a container's row can accept another container
   */
  const canDuplicateSameRow = useCallback((containerId: string): boolean => {
    const container = containers.find(c => c.id === containerId);
    if (!container) return false;
    return canAddToRowIndex(container.rowIndex ?? 0);
  }, [containers, canAddToRowIndex]);

  /**
   * Check if a nested container's row can accept another container
   * This finds the nested container in the hierarchy and checks its parent's nested row capacity
   */
  const canDuplicateNestedInSameRow = useCallback((nestedContainerId: string): boolean => {
    const found = findNestedContainerInHierarchy(containers, nestedContainerId, getNestedContainerRows);
    return found ? canAddToNestedRow(found.parentId, found.rowIndex) : false;
  }, [containers, getNestedContainerRows, canAddToNestedRow]);

  /**
   * Duplicate a container in the same row
   */
  const handleDuplicateInSameRow = useCallback((containerId: string) => {
    const newId = duplicateContainer(containerId, 'same-row', {
      getNextContainerNumber,
      getNestedContainerName,
      canAddToRowIndex,
      canAddToNestedRow,
    });
    if (newId) {
      toast.success('Container duplicated in same row');
    } else {
      toast.error('Failed to duplicate container - row may be full');
    }
  }, [duplicateContainer, getNextContainerNumber, getNestedContainerName, canAddToRowIndex, canAddToNestedRow]);

  /**
   * Duplicate a container in a new row below
   */
  const handleDuplicateInNewRow = useCallback((containerId: string) => {
    const newId = duplicateContainer(containerId, 'new-row', {
      getNextContainerNumber,
      getNestedContainerName,
      canAddToRowIndex,
      canAddToNestedRow,
    });
    if (newId) {
      toast.success('Container duplicated in new row');
    } else {
      toast.error('Failed to duplicate container');
    }
  }, [duplicateContainer, getNextContainerNumber, getNestedContainerName, canAddToRowIndex, canAddToNestedRow]);

  /**
   * Add container at a specific position within a row
   * Used by vertical buttons between containers
   */
  const handleAddContainerAtPosition = useCallback(
    (rowIndex: number, insertAtOrder: number) => {
      if (!canAddToRowIndex(rowIndex)) {
        toast.warning(`Row is full (max ${MAX_CONTAINERS_PER_ROW} containers per row)`);
        return;
      }

      const result = addContainerToRow(rowIndex, `Container ${getNextContainerNumber()}`, { insertAtOrder });

      if (result.success) {
        toast.success(`Container added to row ${rowIndex}`);
      } else {
        const errorMessage = result.validation.errors[0]?.message || 'Failed to add container';
        toast.error(errorMessage);
      }
    },
    [canAddToRowIndex, addContainerToRow, getNextContainerNumber, MAX_CONTAINERS_PER_ROW]
  );

  /**
   * Add container after row (horizontal button)
   * Always creates a new row
   */
  const handleAddContainerHorizontal = useCallback(
    (afterRowIndex: number) => {
      const result = addContainerAfterRow(afterRowIndex, `Container ${getNextContainerNumber()}`);

      if (result.success) {
        toast.success('New row created');
      } else {
        const errorMessage = result.validation.errors[0]?.message || 'Failed to add container';
        toast.error(errorMessage);
      }
    },
    [addContainerAfterRow, getNextContainerNumber]
  );

  /**
   * Add nested container at a specific position within a nested row
   * Used by vertical "+" buttons between nested containers
   */
  const handleAddNestedContainerAtPosition = useCallback(
    (parentId: string, rowIndex: number, insertAtOrder: number) => {
      if (!canAddToNestedRow(parentId, rowIndex)) {
        toast.warning(`Nested row is full (max ${MAX_CONTAINERS_PER_ROW} containers)`);
        return;
      }

      const result = addNestedContainer(parentId, getNestedContainerName(parentId), {
        rowIndex,
        insertAtOrder,
      });

      if (result.success) {
        toast.success('Nested container added');
      } else {
        const errorMessage = result.validation.errors[0]?.message || 'Failed to add nested container';
        toast.error(errorMessage);
      }
    },
    [canAddToNestedRow, addNestedContainer, getNestedContainerName, MAX_CONTAINERS_PER_ROW]
  );

  /**
   * Add nested container after row (horizontal "+" button)
   * Creates a new nested row within parent
   */
  const handleAddNestedContainerHorizontal = useCallback(
    (parentId: string, afterRowIndex: number) => {
      const result = addNestedContainerAfterRow(parentId, afterRowIndex, getNestedContainerName(parentId));

      if (result.success) {
        toast.success('New nested row created');
      } else {
        const errorMessage = result.validation.errors[0]?.message || 'Failed to add nested container';
        toast.error(errorMessage);
      }
    },
    [addNestedContainerAfterRow, getNestedContainerName]
  );

  // Handle item selection within a container
  const handleItemSelect = useCallback(
    (itemId: string, itemType: ItemType, containerId: string) => {
      dispatch({
        type: 'SET_SELECTED_ITEM',
        payload: { itemId, itemType, containerId },
      });
    },
    [dispatch]
  );

  // Handle submodel header click
  const handleSubmodelSelect = useCallback(
    (submodelId: string, containerId: string) => {
      dispatch({
        type: 'SET_SELECTED_SUBMODEL',
        payload: { submodelId, containerId },
      });
    },
    [dispatch]
  );

  // Handle content changes from ContentBlockWidget (via WidgetRenderer)
  const handleContentBlockChange = useCallback(
    (widgetId: string, content: string) => {
      if (!activePageId || !state.model) return;

      const canvasId = Object.keys(state.model.layout.canvas).find(
        (key) => state.model!.layout.canvas[key].pageId === activePageId
      );
      if (!canvasId) return;

      const canvas = state.model.layout.canvas[canvasId];
      if (!canvas) return;

      dispatch({
        type: 'SET_MODEL',
        payload: {
          ...state.model,
          layout: {
            ...state.model.layout,
            canvas: {
              ...state.model.layout.canvas,
              [canvasId]: {
                ...canvas,
                containers: updateWidgetContentInContainers(canvas.containers, widgetId, content),
              },
            },
          },
        },
      });

      dispatch({
        type: 'MARK_DIRTY',
        payload: { section: 'canvas', canvasId },
      });
    },
    [activePageId, state.model, dispatch]
  );

  // Commit an individual item drop to the canvas. Split out of handleDrop so a
  // free-style duplicate can be committed after the user confirms the dialog
  // (the DragEvent is gone by then — insertIndex is captured at drop time).
  const commitItemDrop = useCallback(
    (
      parsedData: Record<string, unknown>,
      containerId: string,
      insertIndex: number,
      asDuplicate: boolean
    ) => {
      const { type, name, widgetType, parentId, submodelName, specId } = parsedData as {
        type: string; name?: string; widgetType?: string;
        parentId?: string; submodelName?: string; specId?: string;
      };

      // Use the model spec ID when present; a free-style duplicate gets an
      // instance-suffixed ID so both copies stay uniquely addressable.
      const itemId = asDuplicate && specId
        ? generateInstanceId(specId)
        : specId || generateId(type);
      const item = createDropItem(parsedData, itemId);
      if (!item) return;

      // yourBuild side effects: store shared widget config + trigger save
      if (type === 'widget' && widgetType === 'yourBuild') {
        // Preserve any config the user already set (e.g. via the header preview
        // panel or a prior instance) instead of clobbering it with fresh defaults.
        const existingConfig = state.model?.layout?.sharedWidgets?.yourBuildWidget;
        const resolvedConfig = existingConfig
          ? mergeYourBuildWithDefaults(existingConfig)
          : createYourBuildConfig();
        if (state.model) {
          dispatch({
            type: 'SET_MODEL',
            payload: {
              ...state.model,
              layout: {
                ...state.model.layout,
                sharedWidgets: {
                  ...state.model.layout?.sharedWidgets,
                  yourBuildWidget: resolvedConfig,
                },
              },
            },
          });
        }
        enqueueSave('sharedWidgets', {
          widgetsData: {
            yourBuildWidget: resolvedConfig,
          },
        });
      }

      // Find the canvasId for this canvas
      const canvasId = Object.keys(state.model?.layout.canvas || {}).find(
        (key) => state.model?.layout.canvas[key].pageId === activePageId
      );

      if (!canvasId) {
        console.warn('Canvas ID not found');
        return;
      }

      // Dispatch ADD_ITEM command with insertIndex
      const command = createAddItemCommand(
        {
          item,
          canvasId,
          containerId,
          insertIndex,
        },
        `Add ${type}: ${name || widgetType}`
      );

      dispatch({ type: 'EXECUTE_COMMAND', payload: command });

      // Set content.submodel when item comes from a submodel (single-field drop).
      // Rule 1/2: the submodel binds to the container that actually HOLDS the
      // field — its own container, whether root or nested — never the parent.
      // Each container locks to its own direct fields independently.
      if (parentId && submodelName) {
        addSubmodelToContainer(containerId, parentId, submodelName);
      }
    },
    [state.model, activePageId, dispatch, enqueueSave, addSubmodelToContainer]
  );

  // Handle drop from Component Library (items and submodels)
  const handleDrop = useCallback(
    (e: React.DragEvent, containerId: string) => {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;

      try {
        const parsedData = JSON.parse(data);

        // Handle submodel drop - add submodel reference + all children
        if (parsedData.type === 'submodel') {
          const { id: submodelId, name: submodelName, children } = parsedData as {
            id: string;
            name: string;
            children: SubmodelChildItem[];
          };

          // Rule 2: Validate that no children are already in container
          if (hasSubmodelChildInContainer(submodelId, containerId)) {
            toast.error('Cannot add: Some items from this submodel are already in this container');
            return;
          }

          // Enforce container↔submodel locking (per-container lock, max>1 cascade,
          // max propagation). Free style lifts all of it — any submodel drops anywhere.
          if (canvas && !freestyle) {
            const lock = canAddSubmodelItemToContainer(
              canvas.containers,
              containerId,
              submodelId,
              (smId) => getSubmodelInstanceLimits(smId).maxInstantiation
            );
            if (!lock.allowed) {
              toast.error(lock.message || 'Cannot add this submodel here.');
              return;
            }
          }

          const success = addSubmodelWithChildren(
            containerId,
            submodelId,
            submodelName,
            children
          );

          if (success) {
            toast.success(`Added ${submodelName} with ${children.length} component(s)`);
          } else {
            toast.error(`Failed to add ${submodelName}`);
          }
          return;
        }

        // Handle individual item drop (existing logic)
        const { type, name, parentId, categoryId } = parsedData as {
          type: string; name?: string; parentId?: string; categoryId?: string;
        };

        if (!canvas) return;

        // Enforce container↔submodel locking on the drop target (per-container lock,
        // max>1 subtree cascade, max propagation). Replaces the old inherited-submodel block.
        // Free style lifts every submodel restriction — mixing and reuse are allowed.
        if (parentId && !freestyle) {
          const lock = canAddSubmodelItemToContainer(
            canvas.containers,
            containerId,
            parentId,
            (smId) => getSubmodelInstanceLimits(smId).maxInstantiation
          );
          if (!lock.allowed) {
            toast.error(lock.message || 'Cannot add this item here.');
            return;
          }

          // Block if submodel is already used in a different root container hierarchy
          if (isSubmodelUsedElsewhere(parentId, containerId)) {
            toast.error('Cannot add: This submodel is already used in another container');
            return;
          }
        }

        // Calculate insert index from cursor position for precise drop placement
        // (read synchronously — the DragEvent is unusable once the dialog defers the commit)
        const insertIndex = calculateInsertIndexFromEvent(e, containerId);

        // Free style: an already-placed item needs the user's "add again?" nod first.
        if (freestyle) {
          const usage =
            type === 'field' && name && isFieldUsed(name, parentId)
              ? getFieldUsageInfo(name, parentId)
              : type === 'category' && categoryId && isCategoryUsed(categoryId, parentId)
                ? getCategoryUsageInfo(categoryId, parentId)
                : type === 'connector' && name && isConnectorUsed(name, parentId)
                  ? getConnectorUsageInfo(name, parentId)
                  : undefined;
          if (usage) {
            setPendingDuplicateDrop({ data: parsedData, containerId, insertIndex, usage });
            return;
          }
        }

        commitItemDrop(parsedData, containerId, insertIndex, false);
      } catch (err) {
        console.error('Error parsing drop data:', err);
      }
    },
    [canvas, freestyle, addSubmodelWithChildren, hasSubmodelChildInContainer, getSubmodelInstanceLimits, isSubmodelUsedElsewhere, isFieldUsed, getFieldUsageInfo, isCategoryUsed, getCategoryUsageInfo, isConnectorUsed, getConnectorUsageInfo, commitItemDrop]
  );

  // Use containerRows from useContainerLayout hook (already grouped and sorted)
  // The hook provides containerRows with proper row grouping and canAddMore status

  if (!activePageId) {
    return (
      <div
        id={baseId}
        data-testid={baseId}
        className="tw-flex-1 tw-h-full tw-bg-surface-sunken tw-flex tw-items-center tw-justify-center"
      >
        <div className="tw-text-center tw-text-ink-tertiary">
          <p className="tw-text-lg">No page selected</p>
          <p className="tw-text-sm">Select a page from navigation to edit</p>
        </div>
      </div>
    );
  }

  if (containerRows.length === 0) {
    return (
      <div
        id={baseId}
        data-testid={baseId}
        className="tw-flex-1 tw-h-full tw-bg-surface-sunken tw-p-4"
      >
        <div className="tw-text-center tw-py-8">
          <p className="tw-text-ink-secondary tw-mb-4">
            No containers yet. Add your first container to get started.
          </p>
          <button data-testid="canvas-canvas-button-11"
            onClick={() => handleAddContainerHorizontal(-1)}
            className="tw-px-4 tw-py-2 tw-bg-accent-bold tw-text-ink-inverse tw-rounded hover:tw-bg-accent-bold-hover"
          >
            + Add Container
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      id={baseId}
      data-testid={baseId}
      className="tw-flex-1 tw-h-full tw-bg-surface-sunken tw-p-4 tw-overflow-auto"
    >
      <div className="tw-space-y-2">
        {containerRows.map((row) => (
          <div key={row.rowIndex}>
            {/* Row of containers with vertical add buttons between them */}
            <div className="tw-flex tw-gap-2">
              {row.containers.map((container, index) => (
                <React.Fragment key={container.id}>
                  <ContainerPlaceholder
                    container={container}
                    onSelect={handleContainerSelect}
                    highlightLevel={getHighlightLevel(container.id)}
                    onDrop={handleDrop}
                    selectedItemId={selectedItemId}
                    onItemSelect={handleItemSelect}
                    nestedRows={getNestedContainerRows(container.id)}
                    canAddToNestedRow={(rowIdx) => canAddToNestedRow(container.id, rowIdx)}
                    onAddNestedToRow={(rowIdx, insertAtOrder) =>
                      handleAddNestedContainerAtPosition(container.id, rowIdx, insertAtOrder)
                    }
                    onAddNestedAfterRow={(afterRowIdx) =>
                      handleAddNestedContainerHorizontal(container.id, afterRowIdx)
                    }
                    selectedContainerId={selectedContainerId}
                    maxNestingDepth={MAX_NESTING_DEPTH}
                    getNestedRowsForContainer={getNestedContainerRows}
                    canAddToNestedRowForContainer={canAddToNestedRow}
                    onAddNestedToRowForContainer={handleAddNestedContainerAtPosition}
                    onAddNestedAfterRowForContainer={handleAddNestedContainerHorizontal}
                    onDeleteContainer={handleDeleteContainer}
                    onDuplicateInSameRow={handleDuplicateInSameRow}
                    onDuplicateInNewRow={handleDuplicateInNewRow}
                    canDuplicateInSameRow={canDuplicateSameRow(container.id)}
                    canDuplicateNestedInSameRow={canDuplicateNestedInSameRow}
                    dragHighlightedContainerId={dragHighlightedContainerId}
                    getHighlightLevel={getHighlightLevel}
                    onSubmodelSelect={handleSubmodelSelect}
                    selectedSubmodelId={selectedSubmodelId}
                    onContentChange={handleContentBlockChange}
                  />
                  {/* Add container button AFTER each container (inserts at order + 1) */}
                  {row.canAddMore && (
                    <AddContainerButton
                      id={`${baseId}-add-vertical-${row.rowIndex}-${index}`}
                      orientation="vertical"
                      onClick={() => handleAddContainerAtPosition(row.rowIndex, container.order + 1)}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Add container below (horizontal line) - creates new row */}
            <AddContainerButton
              id={`${baseId}-add-horizontal-${row.rowIndex}`}
              orientation="horizontal"
              onClick={() => handleAddContainerHorizontal(row.rowIndex)}
            />
          </div>
        ))}
      </div>

      {/* Free style: confirm before adding an item that is already placed somewhere */}
      {pendingDuplicateDrop && (
        <ConfirmationDialog
          id={`${baseId}-duplicate-confirm`}
          isOpen
          title="Component already added"
          message={`'${String(pendingDuplicateDrop.data.name ?? '')}' is already added in ${pendingDuplicateDrop.usage.containerName}${pendingDuplicateDrop.usage.pageName ? ` on ${pendingDuplicateDrop.usage.pageName}` : ''}.`}
          secondaryMessage="Would you like to add it again?"
          confirmText="Add Again"
          cancelText="Cancel"
          onConfirm={() => {
            commitItemDrop(
              pendingDuplicateDrop.data,
              pendingDuplicateDrop.containerId,
              pendingDuplicateDrop.insertIndex,
              true
            );
            setPendingDuplicateDrop(null);
          }}
          onCancel={() => setPendingDuplicateDrop(null)}
        />
      )}
    </div>
  );
};

export default Canvas;
